import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    inject,
    viewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AixModalComponent } from '@trade-platform/ui-components';
import { AixWhatsNewService } from './whats-new.service';

/**
 * The "What's New on AIX" modal (trade-platform/platform-operations#15873). It is NOT in the nav;
 * it opens only from the "Explore New Features" control in a Pylon feature-announcement pop-up,
 * which deep-links the app to `?whatsNew=<content-path>`. The nav bar hosts this modal and, once
 * its view exists, calls `openFromDeepLink(router)` to honor that param (see that method). It renders
 * the roadmap — a standalone, differently-styled static site — inside the app without a second login.
 *
 * Each page is fetched LAZILY from the gated advisors-api endpoint (so content scales with no code
 * change) and rendered in a sandboxed <iframe srcdoc>, which preserves the roadmap's own styling and
 * runs its own JS. In-content links can't be followed inside srcdoc (an iframe navigation can't
 * carry the app's bearer token), so the published pages carry a tiny script that postMessages the
 * target path here; `onMessage` fetches that page and swaps the srcdoc.
 */
@Component({
    selector: 'aix-whats-new-modal',
    templateUrl: './whats-new.html',
    styles: [
        `
            .whats-new-frame {
                display: block;
                width: 100%;
                height: 70vh;
                border: 0;
                background: #fff;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AixModalComponent],
    providers: [AixWhatsNewService]
})
export class AixWhatsNewModalComponent {
    private service = inject(AixWhatsNewService);
    private sanitizer = inject(DomSanitizer);
    private cd = inject(ChangeDetectorRef);

    whatsNewModal = viewChild<AixModalComponent>('whatsNewModal');

    modalTitle = "What's New on AIX";
    // No action buttons; the modal's close "X" (closeable) is the only control.
    buttonOptions: string[] = [];

    /** Trusted HTML for the iframe srcdoc; null until the first page loads. */
    srcdoc: SafeHtml | null = null;
    loading = false;
    error = false;

    /** Session cache of fetched pages (path -> raw HTML) so a re-visit is instant. */
    private cache = new Map<string, string>();

    /**
     * The path of the most recent navigation. A slow response for a superseded page must not
     * overwrite a newer one, so a response only renders while its path is still the pending one.
     */
    private pendingPath: string | null = null;

    /** The frame's window, used to confirm a postMessage really came from our own iframe. */
    private frameWindow: Window | null = null;

    /** Set once a deep link has opened the modal, so `openFromDeepLink` fires at most once. */
    private deepLinkOpened = false;

    /**
     * Open the modal and load `path` (default the landing page). Feature announcements deep-link to
     * a specific page, e.g. `features/support-chat.html`. `loadPage` rejects any non-content path.
     */
    open(path = 'index.html') {
        this.srcdoc = null;
        this.error = false;
        this.whatsNewModal()?.openModal();
        this.loadPage(path);
    }

    /**
     * Open the modal from a "What's New" deep link, at most once per app session. A Pylon feature
     * announcement points the app at `?whatsNew=<content-path>` (readable before or after the hash);
     * the host (nav bar) calls this once the modal's view exists, on first load and on each
     * navigation. No-ops when the param is absent, so ordinary navigation never pops the modal, and
     * the once-guard means re-navigating to the same deep link doesn't reopen it after the user
     * closes it. `open()` -> `loadPage()` validates the path, so a bad `whatsNew` can't escape
     * `/roadmap/`.
     */
    openFromDeepLink(router: Router, search: string = window.location.search) {
        if (this.deepLinkOpened) {
            return;
        }
        const path =
            new URLSearchParams(search).get('whatsNew') ||
            router.parseUrl(router.url).queryParams['whatsNew'];
        if (!path) {
            return;
        }
        this.deepLinkOpened = true;
        this.open(path);
    }

    onFrameLoad(event: Event) {
        this.frameWindow = (event.target as HTMLIFrameElement).contentWindow;
    }

    // The published pages postMessage their link clicks here (see the content build's nav script).
    // Validate the shape AND that the message came from OUR frame before fetching. The source check
    // is strict equality: until the frame has loaded and registered its window, frameWindow is null
    // and every real message (whose source is a real window) is rejected — so another page can't
    // drive this authenticated client during the pre-load window.
    @HostListener('window:message', ['$event'])
    onMessage(event: MessageEvent) {
        const data = event.data;
        if (!data || data.source !== 'aix-roadmap' || data.type !== 'navigate') {
            return;
        }
        if (event.source !== this.frameWindow) {
            return;
        }
        if (typeof data.path === 'string') {
            this.loadPage(data.path);
        }
    }

    // A content path is only ever a relative path to an .html/.txt page under roadmap/. Reject
    // anything that could make `${api}roadmap/${path}` normalise to a DIFFERENT (authenticated) API
    // endpoint client-side — a leading slash, a `..` segment, a `//`, a scheme, or a backslash. A
    // tampered content page could postMessage such a path; the server's safeKey can't catch it
    // because the escaped URL never reaches /roadmap. Mirrors that server-side guard.
    private isContentPath(path: string): boolean {
        if (typeof path !== 'string' || path === '' || path.startsWith('/') || path.includes('\\')) {
            return false;
        }
        if (/^[a-z][a-z0-9+.-]*:/i.test(path)) {
            return false; // a scheme (http:, javascript:, …)
        }
        const clean = path.split('?')[0].split('#')[0];
        if (clean.split('/').some(seg => seg === '..' || seg === '')) {
            return false; // traversal or an empty (//) segment
        }
        return /\.(html|txt)$/i.test(clean);
    }

    private loadPage(path: string) {
        if (!this.isContentPath(path)) {
            return; // never issue an authenticated request outside roadmap/
        }
        this.pendingPath = path;
        const cached = this.cache.get(path);
        if (cached !== undefined) {
            this.render(cached);
            return;
        }
        this.loading = true;
        this.error = false;
        this.cd.detectChanges();
        this.service.getPage(path).subscribe({
            next: html => {
                this.cache.set(path, html);
                // A newer navigation superseded this one -- cache it, but don't overwrite the page
                // the user is now looking at.
                if (this.pendingPath !== path) {
                    return;
                }
                this.loading = false;
                this.render(html);
            },
            error: () => {
                if (this.pendingPath !== path) {
                    return;
                }
                this.loading = false;
                this.error = true;
                this.cd.detectChanges();
            }
        });
    }

    private render(html: string) {
        // First-party trusted content from the gated API. bypass is required or Angular strips the
        // injected nav <script> and the page's own styling. Safe because the frame is sandboxed with
        // allow-scripts but NOT allow-same-origin, so even a tampered page is boxed off from the app.
        this.srcdoc = this.sanitizer.bypassSecurityTrustHtml(html);
        this.cd.detectChanges();
    }
}
